import callApi from '../../Api/config';
import { PACKAGE_TIMELINE, SERVICE_TIMELINE } from '../../constants/EntityName';

function hasTimelineCells(table: unknown): table is string[][] {
  return (
    Array.isArray(table) &&
    table.some(
      (row) => Array.isArray(row) && row.some((cell) => `${cell ?? ''}`.trim()),
    )
  );
}

/** CMS callApi wraps backend as `{ data: { isSuccess, data }, status }`. */
function parseTimelineList(response: any): any[] {
  const payload = response?.data?.data ?? response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(response?.data?.results)) return response.data.results;
  return [];
}

/**
 * Package timeline `table` is often stored as a JSON string inside a json column
 * (double-encoded). Normalize to string[][].
 */
function parseTimelineTable(raw: unknown): string[][] | null {
  let value: unknown = raw;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (hasTimelineCells(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim()) {
      try {
        value = JSON.parse(value);
        continue;
      } catch {
        return null;
      }
    }

    break;
  }

  return null;
}

/** Load timeline from the existing Package/Service Timeline APIs. */
export async function loadExistingTimeline(options: {
  isPackageMode: boolean;
  ownerId?: string;
}): Promise<{ timelineName: string; timelineTable: string[][]; timelineId?: string } | null> {
  const { isPackageMode, ownerId } = options;
  if (!ownerId) return null;

  try {
    const endpoint = isPackageMode
      ? `${PACKAGE_TIMELINE}/package/${ownerId}`
      : `${SERVICE_TIMELINE}/service/${ownerId}`;
    const response = await callApi(endpoint, 'GET');

    // 404 / empty is normal when no timeline exists yet.
    if (response?.status === false && response?.statusCode === 404) {
      return null;
    }

    const first = parseTimelineList(response)[0];
    if (!first) return null;

    const table = parseTimelineTable(first.table);
    if (!table) return null;

    return {
      timelineId: first.id,
      timelineName: first.name || 'Application Timelines',
      timelineTable: table,
    };
  } catch {
    return null;
  }
}

/**
 * Save timeline into the existing Package Timeline / Service Timeline tables
 * (same backend used by Packages → Timeline and Services → Service Timeline).
 */
export async function syncExistingTimeline(options: {
  isPackageMode: boolean;
  ownerId?: string;
  timelineName?: string | null;
  timelineTable?: unknown;
}): Promise<void> {
  const { isPackageMode, ownerId, timelineName, timelineTable } = options;
  if (!ownerId || !hasTimelineCells(timelineTable)) {
    return;
  }

  const baseEndpoint = isPackageMode ? PACKAGE_TIMELINE : SERVICE_TIMELINE;
  const listEndpoint = isPackageMode
    ? `${PACKAGE_TIMELINE}/package/${ownerId}`
    : `${SERVICE_TIMELINE}/service/${ownerId}`;

  let existingId: string | undefined;
  try {
    const listResponse = await callApi(listEndpoint, 'GET');
    existingId = parseTimelineList(listResponse)[0]?.id;
  } catch {
    existingId = undefined;
  }

  const payload: Record<string, unknown> = {
    name: `${timelineName || ''}`.trim() || 'Application Timelines',
    // Existing Package Timeline CMS stores table as a JSON string.
    table: JSON.stringify(timelineTable),
  };

  if (existingId) {
    await callApi(`${baseEndpoint}/${existingId}`, 'PATCH', payload);
    return;
  }

  await callApi(baseEndpoint, 'POST', {
    ...payload,
    ...(isPackageMode
      ? { package: { id: ownerId } }
      : { service: { id: ownerId } }),
  });
}
